import React from 'react'
import { Formik } from 'formik'
import { routes } from '@sketch/modules-common'
import { Link, Section, Skeleton, Text } from '@sketch/components'
import { useGetWorkspaceProfileQuery } from '@sketch/gql-types'
import { PROFILE_URL_PREFIX } from '@sketch/constants'
import {
  getValidationSchema,
  WorkspaceProfileURL,
} from '../../../community/containers/WorkspaceProfileURL'
import { PublishProfileSection } from '../../../community/containers/WorkspaceSettingsPublishProfile'
import { useProfileShortURL } from '../../../community/hooks/useProfileShortURL'
import {
  StyledForm,
  SubmitButton,
  InputWrapper,
} from './WorkspaceSettingsProfile.styles'

interface FormValues {
  shortUrl: string
}

export interface WorkspaceSettingsProfileProps {
  workspaceIdentifier: string
}

interface LoadingGuardProps {
  loading: boolean
  component: React.ReactElement
}

const LoadingGuard: React.FC<LoadingGuardProps> = ({
  loading,
  component: Component,
}) => {
  if (loading) {
    return <Skeleton width="100%" height="70px" />
  }

  return Component
}

interface LabelProps {
  hasNewUrl: boolean
  currentShortUrl: string
}

const Label: React.FC<LabelProps> = ({ hasNewUrl, currentShortUrl }) => {
  if (hasNewUrl) {
    return (
      <Text textStyle="copy.tertiary.standard.E">
        Your current URL is{' '}
        <Link
          href={routes.WORKSPACE_PROFILE.create({
            shortUrlName: currentShortUrl,
          })}
          external
        >
          {PROFILE_URL_PREFIX}
          {currentShortUrl}
        </Link>
      </Text>
    )
  }

  return (
    <Text textStyle="copy.tertiary.standard.E">
      This is your public profile’s URL.{' '}
      <Link
        href={routes.WORKSPACE_PROFILE.create({
          shortUrlName: currentShortUrl,
        })}
        external
      >
        View Profile
      </Link>
    </Text>
  )
}

export const WorkspaceSettingsProfile: React.FC<
  WorkspaceSettingsProfileProps
> = props => {
  const { workspaceIdentifier } = props

  const { data, loading, error } = useGetWorkspaceProfileQuery({
    variables: { workspaceIdentifier },
    fetchPolicy: 'cache-and-network',
  })

  const { identifier, isPublic, shortUrlName } = data?.workspaceProfile ?? {
    identifier: '',
    shortUrlName: '',
  }

  const { updateWorkspaceProfileShortUrl } = useProfileShortURL(shortUrlName!)

  const handleSubmit = (formData: FormValues) => {
    updateWorkspaceProfileShortUrl({
      variables: {
        input: {
          identifier,
          shortUrlName: formData.shortUrl,
        },
      },
    })
  }

  return (
    <>
      <Section title="Visibility">
        <LoadingGuard
          loading={loading}
          component={
            <PublishProfileSection
              error={error}
              isPublic={isPublic}
              identifier={identifier}
            />
          }
        />
      </Section>

      <Section title="Workspace Public Profile URL">
        <LoadingGuard
          loading={loading}
          component={
            <Formik
              initialValues={{ shortUrl: shortUrlName }}
              validationSchema={getValidationSchema('shortUrl')}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {formikBag => {
                const { dirty, values, isValid } = formikBag
                const isEmpty = dirty && values.shortUrl.length === 0

                return (
                  <StyledForm>
                    <WorkspaceProfileURL.Root
                      shortUrl={shortUrlName}
                      name="shortUrl"
                      formikBag={formikBag}
                    >
                      {profileUrlMutationProps => {
                        const {
                          hasNewUrl,
                          isButtonDisabled,
                          isSavingShortUrl,
                          isUrlAvailable,
                        } = profileUrlMutationProps
                        return (
                          <>
                            <InputWrapper>
                              <WorkspaceProfileURL.Input
                                label={
                                  <Label
                                    hasNewUrl={hasNewUrl}
                                    currentShortUrl={shortUrlName}
                                  />
                                }
                                name="shortUrl"
                                formikBag={formikBag}
                                profileShortURLProps={profileUrlMutationProps}
                              />
                              <WorkspaceProfileURL.Error
                                name="shortUrl"
                                formikBag={formikBag}
                                isUrlAvailable={isUrlAvailable}
                              />
                            </InputWrapper>
                            <SubmitButton
                              type="submit"
                              disabled={isButtonDisabled || isEmpty || !isValid}
                              loading={isSavingShortUrl}
                            >
                              Save
                            </SubmitButton>
                          </>
                        )
                      }}
                    </WorkspaceProfileURL.Root>
                  </StyledForm>
                )
              }}
            </Formik>
          }
        />
      </Section>
    </>
  )
}
